import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import IEventState, { ManageEventAction } from '../types/event';
import * as EventAPI from '../api/event';
import * as CorrectionAPI from '../api/correction';
import { EEventState, ICorrection, IMeetingSession } from '@turing/interfaces/event';
import { sortASCTime } from '@turing/helpers';

const initialState: IEventState = {
  loading: false,
  overdueEvents: [],
  todaysEvents: [],
  tomorrowsEvents: [],
  liveEvents: [],
  showSchedule: false,
  selectedEvent: null,
};

export const getMeetingSessionEvents = createAsyncThunk('event/getMeetingSessionEvents', async () => {
  return EventAPI.getMeetingSessionEventsAPI();
});

export const getCorrectionEvents = createAsyncThunk('event/getCorrectionEvents', async () => {
  return CorrectionAPI.getCorrectionEventsAPI();
});

export const eventSlice = createSlice({
  initialState,
  name: ManageEventAction,
  reducers: {
    setSelectedEvent: (state, { payload }) => {
      state.selectedEvent = payload;
    },
    setShowSchedule: (state, { payload }) => {
      state.showSchedule = payload;
    },
  },

  extraReducers: builder => {
    builder.addCase(getMeetingSessionEvents.pending, state => {
      state.loading = true;
    });
    builder.addCase(getMeetingSessionEvents.rejected, (state, { error }) => {
      state.loading = false;
    });
    builder.addCase(getMeetingSessionEvents.fulfilled, (state, action: PayloadAction<IMeetingSession[]>) => {
      state.loading = false;
      const todaysArray: IMeetingSession[] = [];
      const tomorrowsArray: IMeetingSession[] = [];
      const livesArray: IMeetingSession[] = [];
      state.liveEvents = [];
      state.selectedEvent = null;

      const today = new Date();
      const tomorrow = new Date(new Date(today).setDate(today.getDate() + 1));

      for (let index = 0; index < action.payload.length; index++) {
        const event = action.payload[index];
        if (new Date(event.start).toLocaleDateString() === today.toLocaleDateString()) {
          todaysArray.push(event);
          if (new Date().getTime() >= new Date(event.start).getTime() && new Date().getTime() <= new Date(event.end).getTime()) {
            if (state.liveEvents.findIndex(x => x.id === event.id) < 0) {
              livesArray.push(event);
            }
          }
        } else if (new Date(event.start).toLocaleDateString() === tomorrow.toLocaleDateString()) {
          tomorrowsArray.push(event);
        }
      }

      state.todaysEvents = [...todaysArray];
      state.tomorrowsEvents = [...tomorrowsArray];
      state.liveEvents = [...state.liveEvents, ...livesArray];
    });

    builder.addCase(getCorrectionEvents.pending, state => {
      state.loading = true;
    });
    builder.addCase(getCorrectionEvents.rejected, (state, { error }) => {
      state.loading = false;
    });
    builder.addCase(getCorrectionEvents.fulfilled, (state, action: PayloadAction<ICorrection[]>) => {
      state.loading = false;
      const overdueArray: ICorrection[] = [];
      const todaysArray: ICorrection[] = [];
      const tomorrowsArray: ICorrection[] = [];
      const livesArray: ICorrection[] = [];

      const today = new Date();
      const tomorrow = new Date(new Date(today).setDate(today.getDate() + 1));

      for (let index = 0; index < action.payload.length; index++) {
        const event = action.payload[index];
        const isOverdue = event.state !== EEventState.Completed && new Date(event.end).getTime() < new Date().getTime();
        if (isOverdue) {
          if (state.overdueEvents.findIndex(x => x.id === event.id) < 0) {
            overdueArray.push(event);
          }
        } else if (new Date(event.start).toLocaleDateString() === today.toLocaleDateString()) {
          todaysArray.push(event);
          if (new Date().getTime() >= new Date(event.start).getTime() && new Date().getTime() <= new Date(event.end).getTime()) {
            if (state.liveEvents.findIndex(x => x.id === event.id) < 0 && !isOverdue) {
              livesArray.push(event);
            }
          }
        } else if (new Date(event.start).toLocaleDateString() === tomorrow.toLocaleDateString()) {
          tomorrowsArray.push(event);
        }
      }

      state.todaysEvents = sortASCTime([...state.todaysEvents, ...todaysArray]);
      state.tomorrowsEvents = sortASCTime([...state.tomorrowsEvents, ...tomorrowsArray]);
      state.overdueEvents = [...state.overdueEvents, ...overdueArray];
      state.liveEvents = [...state.liveEvents, ...livesArray];
    });
  },
});

export const { setSelectedEvent, setShowSchedule } = eventSlice.actions;
export default eventSlice.reducer;
